import type { MfaMethod } from '../types';

/**
 * Pure MFA helpers (no React, no DOM). Kept free of secrets being logged:
 * these functions only transform values the caller already holds.
 */

/** Extract the shared secret from an `otpauth://totp/...` provisioning URI. */
export function extractTotpSecret(provisioningUri: string): string | null {
	try {
		const uri = new URL(provisioningUri);
		if (uri.protocol !== 'otpauth:') return null;

		const secret = uri.searchParams.get('secret');

		return secret ? secret.replace(/\s+/g, '').toUpperCase() : null;
	} catch {
		return null;
	}
}

/**
 * Normalise user-entered recovery codes: trim, uppercase and drop whitespace.
 * The hyphen inside a code is significant and preserved.
 */
export function normalizeRecoveryCode(raw: string): string {
	return raw.trim().toUpperCase().replace(/\s+/g, '');
}

/** True when an ISO timestamp is in the past. */
export function isExpired(
	expiresAt: string,
	now: number = Date.now(),
): boolean {
	const at = Date.parse(expiresAt);

	return Number.isNaN(at) ? false : at <= now;
}

/** Format codes for a plain-text download, one per line. */
export function formatRecoveryCodes(codes: string[]): string {
	return codes.join('\n');
}

/** Stable, dated filename for the recovery-code download. */
export function recoveryCodesFilename(date: Date = new Date()): string {
	return `recovery-codes-${date.toISOString().slice(0, 10)}.txt`;
}

/**
 * Input attributes that adapt between a 6-digit TOTP code and a recovery
 * code. Shared by every code field so the behaviour stays consistent.
 */
export function getCodeInputProps(method: MfaMethod) {
	return {
		autoComplete: method === 'totp' ? 'one-time-code' : 'off',
		inputMode: method === 'totp' ? 'numeric' : 'text',
		maxLength: method === 'totp' ? 6 : undefined,
		placeholder: method === 'totp' ? '123456' : 'ABCDE-12345',
		className:
			method === 'totp'
				? 'text-center text-lg tracking-[0.4em]'
				: 'font-mono uppercase',
	} as const;
}
